import React, { useMemo, useCallback } from 'react';
import { Table as TableComponent } from '@lucid/lego';
import type { Order } from '../../../../api/services/lupp/order/dto.order';
import { useStore } from '../../../../store/store';
import { createColumns } from './utils';

type TableProps = {
  theme: 'light' | 'dark';
  data: Order[];
  totalCount: number;
  currentPage: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  labels: Record<string, string>;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (column: string, direction: 'asc' | 'desc') => void;
  onOrderSelect?: (orderIdentifier: string | null, orderNumber?: string | null) => void;
};

const Table: React.FC<TableProps> = ({
  theme,
  data,
  totalCount,
  currentPage,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  labels,
  searchQuery,
  onSearchChange,
  sortColumn,
  sortDirection,
  onSort,
  onOrderSelect,
}) => {
  const locale = useStore((state) => state.locale);

  const handleEditClick = useCallback(
    (record: Order) => {
      onOrderSelect?.(record.orderIdentifier, record.orderNumber);
    },
    [onOrderSelect]
  );

  const columns = useMemo(
    () => createColumns({ theme, onEditClick: handleEditClick, labels, locale }),
    [theme, labels, handleEditClick, locale]
  );

  return (
    <TableComponent
      columns={columns}
      data={data}
      theme={theme === 'light' ? 'dark' : 'light'}
      selection={{ enabled: false }}
      pagination={{
        enabled: true,
        total: totalCount,
        current: currentPage,
        pageSize,
        onChange: (page) => onPageChange(page),
        onPageSizeChange,
      }}
      search={{
        placeholder: labels?.['table.searchNameEmail'] ?? 'Search Name / Email / Order Number',
        value: searchQuery,
        onChange: onSearchChange,
      }}
      sort={{
        column: sortColumn,
        direction: sortDirection,
        onSort: onSort,
      }}
      columnConfig={{ enabled: true }}
      emptyState={{
        message: searchQuery.trim()
          ? (labels?.['table.noResults'] ?? 'No results found! Please update your query')
          : (labels?.['table.noOrders'] ?? 'No orders available'),
      }}
      labels={{
        manageColumns: labels?.['table.manageColumns'] ?? 'Manage Columns',
        showingItems:
          labels?.['table.showingItems'] ?? 'Showing {{start}}-{{end}} of {{total}} Items',
        pageOf: labels?.['table.pageOf'] ?? '{{current}} of {{total}}',
        selectAll: labels?.['table.selectAll'] ?? 'Select All',
        clearAll: labels?.['table.clearAll'] ?? 'Clear All',
        columnsVisible:
          labels?.['table.columnsVisible'] ?? '{{visible}} of {{total}} columns visible',
        save: labels?.['table.save'] ?? 'Save',
      }}
      gaComponent="orders-table"
      dataTestId="orders-table"
    />
  );
};

export default Table;
